import { CircleNotch } from "@phosphor-icons/react";
import { Slot, type SlotProps, Slottable } from "@radix-ui/react-slot";
import {
  type ButtonHTMLAttributes,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
} from "react";

import { getWhitelabelCustomization } from "common/helpers/utils";

import { buttonVariants } from "./variants";

import type { VariantProps } from "class-variance-authority";
import type React from "react";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "intent"> & {
    intent?: VariantProps<typeof buttonVariants>["intent"];
    asChild?: boolean;
    isLoading?: boolean;
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;
  };

function Button(
  {
    className,
    variant = "default-black",
    size = "md",
    intent = "default",
    children,
    isLoading = false,
    leftIcon = null,
    rightIcon = null,
    asChild = false,
    disabled,
    ...props
  }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const loadingComponent = <CircleNotch className="animate-spin" />;

  let Comp:
    | React.ForwardRefExoticComponent<
        SlotProps & React.RefAttributes<HTMLElement>
      >
    | "button"
    | "span" = "button";

  if (intent === "labelIcon") {
    Comp = "span";
  } else if (asChild) {
    Comp = Slot;
  }

  if (getWhitelabelCustomization() && variant === "secondary-purple") {
    variant = "secondary-black";
  }

  return (
    <Comp
      className={buttonVariants({
        variant,
        size,
        intent,
        className,
        hasLeftIcon: !!leftIcon,
        hasRightIcon: !!rightIcon,
        hasWhitelabelColor: !!getWhitelabelCustomization()?.primary_color,
      })}
      type="button"
      ref={ref}
      {...props}
      disabled={isLoading || disabled}
    >
      {isLoading && leftIcon ? loadingComponent : leftIcon}

      {intent === "iconOnly" && isLoading ? (
        loadingComponent
      ) : (
        <Slottable>{children}</Slottable>
      )}
      {isLoading &&
      (rightIcon || (!rightIcon && !leftIcon && intent !== "iconOnly"))
        ? loadingComponent
        : rightIcon}
    </Comp>
  );
}

Button.displayName = "Button";

const ButtonWithRef = forwardRef(Button);

export { ButtonWithRef as Button };
